import Layout from "../../components/layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import Accordion from "../../components/v2/accordion/accordion";
import Link from "../../components/Link";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import { GetStaticProps } from "next";
import ComparisonForm from "../../components/v2/form/comparison-form";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import dynamic from "next/dynamic";
import { useState } from "react";
import "../../styles/scss/pricing.module.scss";
import '../../styles/scss/_faq-section.module.scss';
import "../../styles/scss/solutions.module.scss";
import TrustedCompaniesV2 from "../../components/v2/trusted-companies-v2";
import VendorComparisonGrid from "../../components/v2/comparison/vendor-comparison-grid";
import VendorComparisonTable from "../../components/v2/comparison/vendor-comparison-table";
import {
    DYNATRACE_COMPARISON_SECTIONS,
    DYNATRACE_FEATURE_TABLE_ROWS,
    DYNATRACE_SWITCH_STATS,
} from "../../components/v2/comparison/dynatrace-comparison-data";
import VendorSwitchStats from "../../components/v2/comparison/vendor-switch-stats";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import ComparisonVideoTestimonial from "../../components/v2/comparison/comparison-video-testimonial";
import { AIK8sRootIconDark, AutoFixIconDark, OpsAIDarkIcon, AIDashboardIconDark, AIAlertIconDark, ZeroReinstrumentationIcon, IncrementalAdoptionIcon, DataContinuityIcon } from "../../components/v2/icons-v2";

const Dialog = dynamic(() => import("../../components/v2/dialog"), {
    ssr: false,
});

export default function DynaTraceAlternative({ }) {
    const { t } = useTranslation('common');
    const data = t('comparison.dynatrace', { returnObjects: true, defaultValue: {} }) as any;

    const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
    const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(null);

    const handleTestimonialVideoOpen = (src: string) => {
        setTestimonialVideoSrc(src);
        setTestimonialVideoOpen(true);
    };

    const formData = [
        {
            id: 1,
            header: "Zero Re-instrumentation",
            icon: <ZeroReinstrumentationIcon />,

        },
        {
            id: 2,
            header: "Incremental Adoption",
            icon: <IncrementalAdoptionIcon />,

        },
        {
            id: 3,
            header: "100% Data Continuity",
            icon: <DataContinuityIcon />,

        },
    ]

    const faqs = [
        {
            id: 1,
            header: "What's the main difference between Middleware and Dynatrace's pricing models?",
            text: (
                <p>
                    Middleware charges per GB of data ingested. Dynatrace's DPS bills across memory-GiB-hours, host-hours, pod-hours, logs, sessions, and Davis AI capability units against a mandatory annual commit.
                </p>
            ),
        },
        {
            id: 2,
            header:
                "Does Middleware offer a free trial?",
            text: (
                <p>
                    Yes, 14 days, unlimited data ingestion across every feature including OpsAI. No credit card required.
                </p>
            ),
        },
        {
            id: 3,
            header:
                "How does OpsAI compare to Davis AI?",
            text: (
                <p>
                    Davis diagnoses; OpsAI fixes. OpsAI opens a pull request with the code change in Auto Fix mode and applies Kubernetes remediation directly. Auto-resolves 80% of production issues on Middleware's own stack.
                </p>
            ),
        },
        {
            id: 4,
            header: "Do I need to uninstall OneAgent to use Middleware?",
            text: (
                <p>
                    No. OpsAI ingests Dynatrace alerts natively. Run both in parallel, prove ROI, migrate on your schedule.
                </p>
            ),
        },
        {
            id: 5,
            header: "Can I save money by switching from Dynatrace?",
            text: (
                <p>
                    Yes. Customers regularly see 40–60% reductions versus DPS enterprise contracts. Send us your Dynatrace invoice for a line-by-line model.
                </p>
            ),
        },
        {
            id: 6,
            header: "Is Middleware OpenTelemetry-native?",
            text: (
                <p>
                    Yes. OTel is the foundation, not an afterthought. Your instrumentation stays portable.
                </p>
            ),
        },
    ];




    const softwareApplicationSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Middleware Observability Platform",
        "url": "https://middleware.io/",
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.7",
            "reviewCount": "320"
        }
    };

    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://middleware.io/comparison/dynatrace-alternative/",
        "name": "Middleware vs Dynatrace – Dynatrace Alternative",
        "description": "Compare Middleware and Dynatrace across observability capabilities, pricing, and usability.",
        "mainEntity": {
            "@type": "ItemList",
            "itemListOrder": "Unordered",
            "numberOfItems": 2,
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "Middleware Observability Platform",
                        "url": "https://middleware.io/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "Dynatrace",
                        "url": "https://www.dynatrace.com/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                }
            ]
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "url": "https://middleware.io/"
        }
    };


    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(softwareApplicationSchema)
                    }}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(webPageSchema)
                    }}
                />
            </Head>
            <Layout isHomePage={false} pageTitle={"Best and Affordable Dynatrace Alternative | Middleware"}
                ogImage={""}
                uri={"/comparison/dynatrace-alternative/"}
                metaTitle={"Dynatrace Alternative: Why Teams Choose Middleware"}
                metaDesc={"Looking for a Dynatrace alternative? Compare Middleware vs. Dynatrace on pricing, OpenTelemetry support, AI-powered observability, and ease of use."}
                metaRobotsNoindex={"index"}
                metaRobotsNofollow={"follow"}>

                <div className={"mw-vs-datadog solutions"}>
                    <section className={`hero-section solutions-child-page`}>
                        <div className={'container'}>
                            <h1>The Better Dynatrace<span className='text-gradient'> Alternative</span><br /> Full-Stack Observability with an SRE Agent Built In</h1>
                            <p className="mb-4">Tired of DPS invoices you can't forecast, OneAgent lock-in, and a Davis AI that stops at the diagnosis? Switch to Middleware, a unified, OpenTelemetry-native observability platform with predictable pricing, one-minute setup that comes with OpsAI, an SRE Agent that ships the fix as a pull request.</p>

                            <div className={"d-flex"}>
                                <Link href={"/demo-request/"} target={"_blank"}
                                    className={"sign-up ahref-btn primary"}>Request a Demo</Link>
                                <Link href={'#comparison-mw'} className={"sign-up ahref-btn secondary"}>See Comparison</Link>
                            </div>
                        </div>
                    </section>


                    <TrustedCompaniesV2
                        className="home-page-customer"
                        paragraph="Trusted By The Best Teams From"
                    />

                    <VendorComparisonGrid
                        sections={DYNATRACE_COMPARISON_SECTIONS}
                        competitor={{
                            name: "Dynatrace",
                            logoSrc: "/images/comparison/dynatrace-border-logo.svg",
                            logoAlt: "Dynatrace Logo",
                            cellLogoSrc: "/images/dynatrace_logo.svg",
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                    />

                    <CallToActionDocsLink
                        title={'Point your Dynatrace agents at Middleware and switch incrementally'}
                        docsLink="https://docs.middleware.io"
                    />

                    <section className="ai-native-features">
                        <div className="container">
                            <h2>The first observability platform built around AI, <br />
                                not bolted onto it</h2>

                            <section className={'mw-card career'}>
                                <div className={'d-flex card-items'}>
                                    <CardWithIcon
                                        icon={<AIDashboardIconDark />}
                                        title={"AI Dashboards"}
                                        description={
                                            "Describe what you want in plain English. Middleware builds the dashboard, selects the charts, and writes the queries. No PromQL, no custom query language, and no template gallery to comb through."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIAlertIconDark />}
                                        title={"AI Alerts"}
                                        description={
                                            "Alerts that learn what 'normal' looks like for each service and surface only the deviations that matter. Anomaly detection that doesn't drown your on-call team in noise."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<OpsAIDarkIcon />}
                                        title={"OpsAI · SRE Agent"}
                                        description={
                                            "An AI SRE that pinpoints root cause, identifies the offending line of code, and opens a pull request with the fix while you're still reading the alert. Auto-resolves issues with 80% accuracy."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIK8sRootIconDark />}
                                        title={"AI K8s Root Cause Finder"}
                                        description={
                                            "Auto-structuring, severity detection, and pattern recognition built into log ingestion. No manual regular expressions, no parsing pipelines to maintain."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AutoFixIconDark />}
                                        title={"Auto-Fix · 60% Time Saved"}
                                        description={
                                            "Auto-fix issues across infrastructure and application layers with 80% accuracy. Engineers spend 60% less time resolving issues — and more time shipping products."
                                        }
                                    />

                                </div>
                            </section>
                        </div>
                    </section>

                    <div className="customer-stories-comparison">
                        <div className="container">
                            <div className="section-tag testimonial">
                                Testimonials
                            </div>
                            <h2><span className="text-gradient">Customer </span> Stories</h2>


                            <div className="d-flex">
                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/aceturtle.svg" alt="Aceturtle" />
                                    <p>Middleware has played a very good role in transforming our observability and application performance. For instance, we reduced our total observability costs by 50%.</p>
                                    <Link href="/customers/aceturtle/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Vivek Gupta</p>
                                        <p>Founder & CEO</p>
                                    </div>
                                </div>

                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/hotplate.svg" alt="Hotplate" />
                                    <p>We're heavy users of RUM and have tried many tools before discovering Middleware. We've been using OpsAI for the past few weeks, and it has automatically resolved about 60% of our production issues.</p>
                                    <Link href="/customers/hotplate/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Rangaraj Tirumala</p>
                                        <p>Founding Engineer at Hotplate</p>
                                    </div>
                                </div>

                                <ComparisonVideoTestimonial onVideoOpen={handleTestimonialVideoOpen} />
                            </div>

                        </div>
                    </div>

                    <VendorComparisonTable
                        rows={DYNATRACE_FEATURE_TABLE_ROWS}
                        competitor={{
                            name: "Dynatrace",
                            logoSrc: "/images/dynatrace_logo.svg",
                            logoAlt: "Dynatrace Logo",
                            cellLogoSrc: "/images/dynatrace_logo.svg",
                            cellLogoWidth: 90,
                            cellLogoHeight: 22,
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                    />

                    <VendorSwitchStats content={DYNATRACE_SWITCH_STATS} />

                    <ComparisonForm title={"You Don't Have to Rip Out Dynatrace on Day One"} label={"Dynatrace"} formId={"88119452-6938-4b3d-8cd9-f3737cf4e132"} description={"Already running OneAgent? Forward it to OpsAI and let it investigate & fix incidents against your existing stack without re-instrumentation"} isDatadog={true} formDataComparison={formData} docsLink="https://docs.middleware.io" />

                    <section className={"faq-section"}>
                        <div className={"container"}>
                            <h2>{String(data?.faq?.title ?? 'FAQs')}</h2>
                            <p>{String(data?.faq?.subtitle ?? '')}</p>
                            <div className={"faq-item"}>
                                <Accordion faqs={faqs} showGroups={true} />
                            </div>
                        </div>
                    </section>

                    <CallToActionSmall />

                </div>
            </Layout>

            <Dialog
                isOpen={testimonialVideoOpen}
                position="center"
                onClose={() => {
                    setTestimonialVideoOpen(false);
                    setTestimonialVideoSrc(null);
                }}
                size="md"
                verticalCenter={true}
            >
                <div style={{ textAlign: "center" }}>
                    {testimonialVideoSrc ? (
                        <video
                            key={testimonialVideoSrc}
                            playsInline
                            preload="none"
                            autoPlay
                            width="100%"
                            height={450}
                            controls={true}
                        >
                            <source src={testimonialVideoSrc} type="video/mp4" />
                        </video>
                    ) : null}
                </div>
            </Dialog>
        </>
    );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    const translations = await serverSideTranslations(
        ctx?.locale ?? "en",
        ["common", "header", "footer"],
        nextI18NextConfig,
    );
    return {
        props: {
            ...translations,
            pageTitle: "Dynatrace Alternative: Why Teams Choose Middleware",
            description: "Looking for a Dynatrace alternative? Compare Middleware vs. Dynatrace on pricing, OpenTelemetry support, AI-powered observability, and ease of use.",
            ogImage: "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png",
            uri: "/comparison/dynatrace-alternative/",
            metaTitle: "Dynatrace Alternative: Why Teams Choose Middleware",
            metaDesc: "Looking for a Dynatrace alternative? Compare Middleware vs. Dynatrace on pricing, OpenTelemetry support, AI-powered observability, and ease of use.",
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
        },
    }
}